import { useTranslation } from 'react-i18next';
import { E2NTableCell } from '../../../../components/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeftToBracket,
  faClock,
} from '@fortawesome/pro-light-svg-icons';
import { Pause, Puenktlichkeit } from '../../entities';
import { Abweichung } from '../Abweichung';
import { getTimeDisplayFromDate } from '../../../../utils';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui';
import { PausenHoverCard } from '@/domain/arbeitszeiten/components/PausenHoverCard';

// eslint-disable-next-line max-lines-per-function, complexity
export const ArbeitszeitCell = function ArbeitszeitCell({
  beginn,
  ende,
  dauer,
  puenktlichkeit,
  kommen,
  gehen,
  pausen,
}: {
  beginn: Date;
  ende: Date;
  dauer: number;
  puenktlichkeit: Puenktlichkeit;
  kommen?: Date;
  gehen?: Date;
  pausen: Pause[];
}) {
  const { t } = useTranslation();

  return (
    <E2NTableCell className="font-semibold w-[30%]" isBorderVisible>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1 items-start">
          {beginn !== null ? (
            <div className="flex flex-row gap-3 items-center">
              <span>{getTimeDisplayFromDate(beginn)}</span>
              {kommen !== null && kommen !== undefined && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger>
                      <div className="flex gap-1 items-center text-neutrals-500">
                        <FontAwesomeIcon
                          icon={faArrowLeftToBracket}
                          size="sm"
                        />
                        <span className="text-xs text-neutrals-500">
                          {getTimeDisplayFromDate(kommen)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(`domain.arbeitszeiten.uebersicht.kommenGestempelt`)}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          ) : (
            <Badge className="bg-primary-orange-500 hover:bg-primary-orange-500 w-28 flex justify-center">
              {t('domain.arbeitszeiten.uebersicht.keineBuchung')}
            </Badge>
          )}
          {puenktlichkeit.abweichungBeginn !== undefined && (
            <Abweichung abweichung={puenktlichkeit.abweichungBeginn} />
          )}
        </div>
        {dauer !== 0 ? (
          <>
            <div className="h-px bg-neutrals-500 grow" />
            <PausenHoverCard pausen={pausen}>
              <span className="text-xs text-neutrals-700">
                {dauer} {t('general.date.minutes')}
              </span>
            </PausenHoverCard>
            <div className="h-px bg-neutrals-500 grow" />
          </>
        ) : (
          <div className="h-px bg-neutrals-500 grow" />
        )}

        <div className="flex flex-col gap-1 items-start">
          {ende !== null ? (
            <div className="flex flex-row gap-3 items-center">
              <span>{getTimeDisplayFromDate(ende)}</span>
              {gehen !== null && gehen !== undefined && dauer === 0 && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <div className="flex gap-1 items-center text-neutrals-500">
                        <FontAwesomeIcon
                          icon={faArrowLeftToBracket}
                          size="sm"
                        />
                        <span className="text-xs">
                          {getTimeDisplayFromDate(gehen)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(`domain.arbeitszeiten.uebersicht.gehenGestempelt`)}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          ) : (
            <Ende datum={beginn} />
          )}

          {puenktlichkeit.abweichungEnde !== undefined &&
            puenktlichkeit.abweichungEnde !== null && (
              <Abweichung abweichung={puenktlichkeit.abweichungEnde} />
            )}
        </div>
      </div>
    </E2NTableCell>
  );
};

export function Ende({ datum }: { datum: Date }) {
  const { t } = useTranslation();

  const today = new Date();
  if (
    datum != null &&
    datum < today &&
    today.getTime() - datum.getTime() > 24 * 60 * 60 * 1000
  ) {
    return (
      <Badge className="bg-primary-orange-500 hover:bg-primary-orange-500 w-28 flex justify-center">
        {t('domain.arbeitszeiten.uebersicht.keineBuchung')}
      </Badge>
    );
  }
  return <FontAwesomeIcon icon={faClock} size="lg" />;
}
